import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Download, Trash2, Upload, Pencil, RefreshCw } from 'lucide-react';

const LIST_BADGES = {
  site: { label: 'Site', background: '#dcfce7', color: '#166534' },
  legacy: { label: 'Legacy', background: '#fef9c3', color: '#854d0e' },
  beach_bum_society: { label: 'Beach Bum Society', background: '#dbeafe', color: '#1e40af' },
  customers: { label: 'Customers', background: '#f3e8ff', color: '#6b21a8' },
};

const TAGGED_LISTS = ['legacy', 'beach_bum_society', 'customers'];

// Which lists a subscriber is on: their home list (source — anything that isn't a
// tagged list counts as the main Site list) plus any extra tagged lists.
function subscriberLists(subscriber) {
  const home = TAGGED_LISTS.includes(subscriber.source) ? subscriber.source : 'site';
  return [home, ...(subscriber.lists || []).filter((l) => l !== home && TAGGED_LISTS.includes(l))];
}

const SIGNUP_SOURCE_LABELS = {
  post_form: 'End-of-post form',
  subscribe_page: 'Follow page',
};

const SUMMARY_DAYS = 90;

function topCounts(values, limit = 5) {
  const counts = {};
  values.forEach((v) => { counts[v] = (counts[v] || 0) + 1; });
  return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, limit);
}

// Where signups have come from recently — only counts signups made since
// tracking started (older and imported subscribers have no signup_source).
function SignupSourcesSummary({ subscribers }) {
  const cutoff = Date.now() - SUMMARY_DAYS * 24 * 60 * 60 * 1000;
  const tracked = subscribers.filter(
    (s) => s.signup_source && new Date(s.subscribed_at).getTime() >= cutoff
  );
  if (tracked.length === 0) return null;

  const columns = [
    { title: 'Form', rows: topCounts(tracked.map((s) => SIGNUP_SOURCE_LABELS[s.signup_source] || s.signup_source)) },
    { title: 'Page they were on', rows: topCounts(tracked.map((s) => s.signup_page || '(arrived directly)')) },
    { title: 'Came from', rows: topCounts(tracked.map((s) => s.signup_referrer || 'Direct / unknown')) },
  ];

  return (
    <div className="card" style={{ marginBottom: '20px', padding: '16px 20px' }}>
      <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '12px' }}>
        Where signups came from — last {SUMMARY_DAYS} days ({tracked.length})
      </div>
      <div style={{ display: 'flex', gap: '28px', flexWrap: 'wrap' }}>
        {columns.map(({ title, rows }) => (
          <div key={title} style={{ flex: '1 1 200px', minWidth: 0 }}>
            <div style={{ fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
              {title}
            </div>
            {rows.map(([label, count]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '13px', padding: '2px 0' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={label}>{label}</span>
                <strong>{count}</strong>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function Subscribers({ onLogout }) {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showLegacyImportDialog, setShowLegacyImportDialog] = useState(false);
  const [showBBSImportDialog, setShowBBSImportDialog] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [importing, setImporting] = useState(false);
  const [importingLegacy, setImportingLegacy] = useState(false);
  const [importingBBS, setImportingBBS] = useState(false);
  const [editingSubscriber, setEditingSubscriber] = useState(null);
  const [editForm, setEditForm] = useState({ email: '', firstname: '', lastname: '', frequency: 'weekly', lists: [] });
  const [saving, setSaving] = useState(false);
  const [listFilter, setListFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('add');
  const [bulkList, setBulkList] = useState('site');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [syncingCustomers, setSyncingCustomers] = useState(false);
  const fileInputRef = useRef(null);
  const legacyFileInputRef = useRef(null);
  const bbsFileInputRef = useRef(null);

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      // Fetch both subscribers and supporters
      const [subscribersRes, supportersRes] = await Promise.all([
        axios.get('/subscribers'),
        axios.get('/supporters')
      ]);
      
      // Create a map of supporter emails to their plans
      const supporterPlans = {};
      supportersRes.data.forEach(s => {
        supporterPlans[s.email] = s.plan || 'supporter';
      });
      
      // Merge plan info into subscribers
      const mergedSubscribers = subscribersRes.data.map(sub => ({
        ...sub,
        plan: supporterPlans[sub.email] || 'free'
      }));
      
      setSubscribers(mergedSubscribers);
    } catch (error) {
      toast.error('Failed to fetch subscribers');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubscriber = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/subscribers', { email: newEmail });
      toast.success('Subscriber added');
      setShowDialog(false);
      setNewEmail('');
      fetchSubscribers();
    } catch (error) {
      toast.error('Failed to add subscriber');
    }
  };

  const openEdit = (subscriber) => {
    setEditingSubscriber(subscriber);
    setEditForm({
      email: subscriber.email,
      firstname: subscriber.firstname || '',
      lastname: subscriber.lastname || '',
      frequency: subscriber.frequency === 'daily' ? 'daily' : 'weekly',
      lists: subscriberLists(subscriber)
    });
  };

  const toggleEditList = (list) => {
    setEditForm((form) => ({
      ...form,
      lists: form.lists.includes(list) ? form.lists.filter((l) => l !== list) : [...form.lists, list]
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (editForm.lists.length === 0) {
      toast.error('Pick at least one list (or delete the subscriber instead)');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`/subscribers/${editingSubscriber.id}`, {
        email: editForm.email.trim(),
        firstname: editForm.firstname.trim() || null,
        lastname: editForm.lastname.trim() || null,
        frequency: editForm.frequency
      });
      const before = subscriberLists(editingSubscriber);
      const listsChanged = before.length !== editForm.lists.length || before.some((l) => !editForm.lists.includes(l));
      if (listsChanged) {
        await axios.put(`/subscribers/${editingSubscriber.id}/lists`, { lists: editForm.lists });
      }
      toast.success('Subscriber updated');
      setEditingSubscriber(null);
      fetchSubscribers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscriber');
    } finally {
      setSaving(false);
    }
  };

  const visibleSubscribers = listFilter === 'all'
    ? subscribers
    : subscribers.filter((s) => subscriberLists(s).includes(listFilter));
  const allVisibleSelected = visibleSubscribers.length > 0 && visibleSubscribers.every((s) => selectedIds.has(s.id));

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      visibleSubscribers.forEach((s) => { if (allVisibleSelected) next.delete(s.id); else next.add(s.id); });
      return next;
    });
  };

  const handleBulkApply = async () => {
    const count = selectedIds.size;
    if (bulkAction === 'daily' || bulkAction === 'weekly') {
      const label = bulkAction === 'daily' ? 'every new story (daily)' : 'the weekly roundup';
      if (!window.confirm(`Set ${count} subscriber(s) to ${label}?`)) return;
      setBulkSaving(true);
      try {
        const { data } = await axios.post('/subscribers/bulk-frequency', {
          subscriber_ids: [...selectedIds],
          frequency: bulkAction
        });
        toast.success(`${data.changed} updated${data.unchanged ? `, ${data.unchanged} already set` : ''}`);
        setSelectedIds(new Set());
        fetchSubscribers();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update frequency');
      } finally {
        setBulkSaving(false);
      }
      return;
    }
    const listLabel = LIST_BADGES[bulkList].label;
    const question = bulkAction === 'add' ? `Add ${count} subscriber(s) to ${listLabel}?`
      : bulkAction === 'remove' ? `Remove ${count} subscriber(s) from ${listLabel}?`
      : `Move ${count} subscriber(s) to ${listLabel} only? They'll be taken off every other list.`;
    if (!window.confirm(question)) return;

    setBulkSaving(true);
    try {
      const { data } = await axios.post('/subscribers/bulk-lists', {
        subscriber_ids: [...selectedIds],
        action: bulkAction,
        target_list: bulkList
      });
      const parts = [`${data.changed} updated`];
      if (data.unchanged) parts.push(`${data.unchanged} already set`);
      if (data.skipped) parts.push(`${data.skipped} skipped (it was their only list)`);
      toast.success(parts.join(', '));
      setSelectedIds(new Set());
      fetchSubscribers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update lists');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleSyncCustomers = async () => {
    setSyncingCustomers(true);
    try {
      const { data } = await axios.post('/admin/subscribers/sync-customers');
      toast.success(data.message);
      fetchSubscribers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync customers');
    } finally {
      setSyncingCustomers(false);
    }
  };

  const handleRemove = async (email) => {
    if (!window.confirm(`Remove ${email} from subscribers?`)) return;
    
    try {
      await axios.delete(`/subscribers/${email}`);
      toast.success('Subscriber removed');
      fetchSubscribers();
    } catch (error) {
      toast.error('Failed to remove subscriber');
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get('/subscribers/export', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'subscribers.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Subscribers exported');
    } catch (error) {
      toast.error('Failed to export subscribers');
    }
  };

  const handleImportCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setImporting(true);
    
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      // Skip header row if it contains "email"
      const startIndex = lines[0].toLowerCase().includes('email') ? 1 : 0;
      
      let imported = 0;
      let skipped = 0;
      
      for (let i = startIndex; i < lines.length; i++) {
        const columns = lines[i].split(',').map(col => col.trim().replace(/"/g, ''));
        const email = columns[0]; // First column is email
        const firstName = columns[1] || null; // Second column is first name
        const lastName = columns[2] || null; // Third column is last name
        
        // Basic email validation - skip rows with empty or invalid emails
        if (email && email.includes('@') && email.includes('.')) {
          try {
            await axios.post('/subscribers', {
              email,
              firstname: firstName,
              lastname: lastName
            });
            imported++;
          } catch (error) {
            // Already exists or invalid
            skipped++;
          }
        } else {
          skipped++;
        }
      }
      
      toast.success(`Imported ${imported} subscribers${skipped > 0 ? `, ${skipped} skipped` : ''}`);
      setShowImportDialog(false);
      fetchSubscribers();
    } catch (error) {
      toast.error('Failed to import CSV');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImportLegacy = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    setImportingLegacy(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post('/subscribers/import-legacy', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(response.data.message);
      setShowLegacyImportDialog(false);
      fetchSubscribers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import legacy list');
    } finally {
      setImportingLegacy(false);
      if (legacyFileInputRef.current) legacyFileInputRef.current.value = '';
    }
  };

  const handleImportBBS = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    setImportingBBS(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post('/subscribers/import-beach-bum-society', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(response.data.message);
      setShowBBSImportDialog(false);
      fetchSubscribers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import Beach Bum Society list');
    } finally {
      setImportingBBS(false);
      if (bbsFileInputRef.current) bbsFileInputRef.current.value = '';
    }
  };

  return (
    <AdminLayout onLogout={onLogout} currentPage="subscribers">
      <div data-testid="subscribers-page">
        <div className="page-header">
          <h1>Subscribers</h1>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <Button
              variant="outline"
              onClick={handleSyncCustomers}
              disabled={syncingCustomers}
              title="Add everyone with a paid order to the Customers list. New customers are added automatically."
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {syncingCustomers ? 'Syncing...' : 'Sync customers from orders'}
            </Button>
            <Button
              variant="outline"
              data-testid="export-subscribers-btn"
              onClick={handleExport}
              disabled={subscribers.length === 0}
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            
            {/* Import CSV Dialog */}
            <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="import-subscribers-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Import CSV
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Import Subscribers from CSV</DialogTitle>
                </DialogHeader>
                <div style={{ padding: '10px 0' }}>
                  <p style={{ marginBottom: '15px', color: '#666', fontSize: '14px' }}>
                    Upload a CSV file with subscriber info:
                  </p>
                  <div style={{ background: '#f5f5f5', padding: '15px', borderRadius: '8px', marginBottom: '20px', fontFamily: 'monospace', fontSize: '13px' }}>
                    <strong>Column format:</strong><br />
                    email, firstname, lastname<br /><br />
                    <strong>Example:</strong><br />
                    email,firstname,lastname<br />
                    john@example.com,John,Doe<br />
                    jane@example.com,Jane,Smith
                  </div>
                  <p style={{ marginBottom: '15px', color: '#888', fontSize: '13px' }}>
                    Rows without valid emails will be skipped.
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleImportCSV}
                    style={{ display: 'none' }}
                    data-testid="csv-file-input"
                  />
                  <Button 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={importing}
                    style={{ width: '100%' }}
                  >
                    {importing ? 'Importing...' : 'Select CSV File'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Import Legacy List Dialog */}
            <Dialog open={showLegacyImportDialog} onOpenChange={setShowLegacyImportDialog}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Upload className="w-4 h-4 mr-2" />
                  Import Legacy List
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Import Legacy Subscriber List</DialogTitle>
                </DialogHeader>
                <div style={{ padding: '10px 0' }}>
                  <p style={{ marginBottom: '12px', color: '#444', fontSize: '14px' }}>
                    These emails will be tagged as <strong>Legacy</strong> and kept separate from your new site subscribers. No welcome email will be sent.
                  </p>
                  <div style={{ background: '#f5f5f5', padding: '15px', borderRadius: '8px', marginBottom: '20px', fontFamily: 'monospace', fontSize: '13px' }}>
                    <strong>Column format:</strong><br />
                    email, firstname, lastname<br /><br />
                    <strong>Example:</strong><br />
                    email,firstname,lastname<br />
                    john@example.com,John,Doe<br />
                    jane@example.com,Jane,Smith
                  </div>
                  <p style={{ marginBottom: '15px', color: '#888', fontSize: '13px' }}>
                    Emails already in the system will be skipped.
                  </p>
                  <input
                    ref={legacyFileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleImportLegacy}
                    style={{ display: 'none' }}
                  />
                  <Button
                    onClick={() => legacyFileInputRef.current?.click()}
                    disabled={importingLegacy}
                    style={{ width: '100%' }}
                  >
                    {importingLegacy ? 'Importing...' : 'Select CSV File'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Import Beach Bum Society Dialog */}
            <Dialog open={showBBSImportDialog} onOpenChange={setShowBBSImportDialog}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Upload className="w-4 h-4 mr-2" />
                  Import Beach Bum Society
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Import Beach Bum Society List</DialogTitle>
                </DialogHeader>
                <div style={{ padding: '10px 0' }}>
                  <p style={{ marginBottom: '12px', color: '#444', fontSize: '14px' }}>
                    These emails will be tagged as <strong>Beach Bum Society</strong> and kept as their own group — separate from
                    site subscribers and the legacy list, so you can send to just them. No welcome email will be sent.
                  </p>
                  <div style={{ background: '#f5f5f5', padding: '15px', borderRadius: '8px', marginBottom: '20px', fontFamily: 'monospace', fontSize: '13px' }}>
                    <strong>Column format:</strong><br />
                    email, firstname, lastname<br /><br />
                    <strong>Example:</strong><br />
                    email,firstname,lastname<br />
                    john@example.com,John,Doe<br />
                    jane@example.com,Jane,Smith
                  </div>
                  <p style={{ marginBottom: '15px', color: '#888', fontSize: '13px' }}>
                    Emails already in the system will be skipped.
                  </p>
                  <input
                    ref={bbsFileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleImportBBS}
                    style={{ display: 'none' }}
                  />
                  <Button
                    onClick={() => bbsFileInputRef.current?.click()}
                    disabled={importingBBS}
                    style={{ width: '100%' }}
                  >
                    {importingBBS ? 'Importing...' : 'Select CSV File'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={showDialog} onOpenChange={setShowDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-subscriber-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Subscriber
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Subscriber</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddSubscriber} data-testid="add-subscriber-form">
                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      data-testid="subscriber-email-input"
                      className="form-input"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      required
                      autoFocus
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowDialog(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="save-subscriber-btn">
                      Add Subscriber
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Edit Subscriber Dialog */}
        <Dialog open={!!editingSubscriber} onOpenChange={(open) => !open && setEditingSubscriber(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Subscriber</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSubmit}>
              <div className="form-group">
                <label htmlFor="edit-email">Email Address</label>
                <input
                  type="email"
                  id="edit-email"
                  className="form-input"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  required
                  autoFocus
                />
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="edit-firstname">First Name</label>
                  <input
                    type="text"
                    id="edit-firstname"
                    className="form-input"
                    value={editForm.firstname}
                    onChange={(e) => setEditForm({ ...editForm, firstname: e.target.value })}
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="edit-lastname">Last Name</label>
                  <input
                    type="text"
                    id="edit-lastname"
                    className="form-input"
                    value={editForm.lastname}
                    onChange={(e) => setEditForm({ ...editForm, lastname: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="edit-frequency">Frequency</label>
                <select
                  id="edit-frequency"
                  className="form-input"
                  value={editForm.frequency}
                  onChange={(e) => setEditForm({ ...editForm, frequency: e.target.value })}
                >
                  <option value="weekly">Weekly roundup (Mondays)</option>
                  <option value="daily">Daily (every new story)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Lists</label>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  {Object.entries(LIST_BADGES).map(([list, badge]) => (
                    <label key={list} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 'normal', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={editForm.lists.includes(list)}
                        onChange={() => toggleEditList(list)}
                      />
                      {badge.label}
                    </label>
                  ))}
                </div>
                <p style={{ fontSize: '12px', color: '#888', margin: '6px 0 0' }}>
                  Site = your posts, at the frequency above. Legacy, Beach Bum Society and Customers only get emails you send to that list.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <Button type="button" variant="outline" onClick={() => setEditingSubscriber(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {!loading && <SignupSourcesSummary subscribers={subscribers} />}

        {loading ? (
          <div className="loading-spinner"></div>
        ) : subscribers.length === 0 ? (
          <div className="empty-state" data-testid="empty-subscribers">
            <h3>No subscribers yet</h3>
            <p>Add subscribers manually or share your subscribe form</p>
          </div>
        ) : (
          <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
              Show
              <select className="form-input" style={{ width: 'auto', padding: '6px 10px' }}
                value={listFilter} onChange={(e) => setListFilter(e.target.value)}>
                <option value="all">All lists ({subscribers.length})</option>
                {Object.entries(LIST_BADGES).map(([list, badge]) => (
                  <option key={list} value={list}>
                    {badge.label} ({subscribers.filter((s) => subscriberLists(s).includes(list)).length})
                  </option>
                ))}
              </select>
            </label>
            {selectedIds.size > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
                background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '6px 12px'
              }}>
                <strong style={{ fontSize: '14px' }}>{selectedIds.size} selected</strong>
                <select className="form-input" style={{ width: 'auto', padding: '6px 10px' }}
                  value={bulkAction} onChange={(e) => setBulkAction(e.target.value)}>
                  <option value="add">Add to</option>
                  <option value="remove">Remove from</option>
                  <option value="move">Move to (only)</option>
                  <option value="weekly">Set to weekly</option>
                  <option value="daily">Set to daily</option>
                </select>
                {bulkAction !== 'daily' && bulkAction !== 'weekly' && (
                  <select className="form-input" style={{ width: 'auto', padding: '6px 10px' }}
                    value={bulkList} onChange={(e) => setBulkList(e.target.value)}>
                    {Object.entries(LIST_BADGES).map(([list, badge]) => (
                      <option key={list} value={list}>{badge.label}</option>
                    ))}
                  </select>
                )}
                <Button size="sm" onClick={handleBulkApply} disabled={bulkSaving}>
                  {bulkSaving ? 'Applying...' : 'Apply'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setSelectedIds(new Set())}>
                  Clear
                </Button>
              </div>
            )}
          </div>
          <div className="table-container">
            <table className="table" data-testid="subscribers-table">
              <thead>
                <tr>
                  <th style={{ width: '36px' }}>
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      aria-label="Select all shown"
                      title="Select all shown"
                    />
                  </th>
                  <th>Email</th>
                  <th>Name</th>
                  <th>List</th>
                  <th>Frequency</th>
                  <th>Plan</th>
                  <th>Subscribed At</th>
                  <th>Signed Up From</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleSubscribers.map((subscriber) => (
                  <tr key={subscriber.id} data-testid={`subscriber-row-${subscriber.email}`}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(subscriber.id)}
                        onChange={() => toggleSelected(subscriber.id)}
                        aria-label={`Select ${subscriber.email}`}
                      />
                    </td>
                    <td>{subscriber.email}</td>
                    <td style={{ color: subscriber.firstname ? 'inherit' : '#999' }}>
                      {subscriber.firstname || subscriber.lastname
                        ? `${subscriber.firstname || ''} ${subscriber.lastname || ''}`.trim()
                        : '—'}
                    </td>
                    <td>
                      {subscriberLists(subscriber).map((list) => {
                        const badge = LIST_BADGES[list];
                        return (
                          <span key={list} style={{
                            display: 'inline-block',
                            padding: '3px 10px',
                            margin: '2px 4px 2px 0',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            background: badge.background,
                            color: badge.color,
                          }}>
                            {badge.label}
                          </span>
                        );
                      })}
                    </td>
                    <td>
                      <span style={{
                        display: 'inline-block',
                        padding: '3px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: subscriber.frequency === 'daily' ? '#dbeafe' : '#f3f4f6',
                        color: subscriber.frequency === 'daily' ? '#1e40af' : '#6b7280',
                      }}>
                        {subscriber.frequency === 'daily' ? 'Daily' : 'Weekly'}
                      </span>
                    </td>
                    <td>
                      <span style={{
                        display: 'inline-block',
                        padding: '3px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: subscriber.plan === 'annual' ? '#fef3c7' :
                                   subscriber.plan === 'monthly' ? '#dbeafe' : 
                                   '#f3f4f6',
                        color: subscriber.plan === 'annual' ? '#92400e' : 
                               subscriber.plan === 'monthly' ? '#1e40af' : 
                               '#6b7280'
                      }}>
                        {subscriber.plan === 'annual' ? 'Annual' : 
                         subscriber.plan === 'monthly' ? 'Monthly' : 
                         'Free'}
                      </span>
                    </td>
                    <td>{new Date(subscriber.subscribed_at).toLocaleDateString()}</td>
                    <td style={{ fontSize: '12px', color: '#666', maxWidth: '220px' }}>
                      {subscriber.signup_source ? (
                        <>
                          <div style={{ fontWeight: '600', color: '#333' }}>{SIGNUP_SOURCE_LABELS[subscriber.signup_source]}</div>
                          {subscriber.signup_page && <div style={{ wordBreak: 'break-all' }}>{subscriber.signup_page}</div>}
                          {subscriber.signup_referrer && <div>via {subscriber.signup_referrer}</div>}
                        </>
                      ) : (
                        <span style={{ color: '#bbb' }}>—</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`edit-subscriber-${subscriber.email}`}
                          onClick={() => openEdit(subscriber)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`remove-subscriber-${subscriber.email}`}
                          onClick={() => handleRemove(subscriber.email)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

export default Subscribers;